import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
dayjs.extend(isSameOrBefore);

const countCumpleanerosTickets = (tickets) => {
  let count = 0;

  tickets.forEach((ticket) => {
    const usuario = ticket.pre_usuarios;
    if (!usuario) return;

    const tipo = usuario.pre_tipos_usuarios?.tipo;
    const fechaNaci = usuario.fecha_naci;
    const createAt = dayjs(ticket.create_at).tz("America/Guayaquil");

    // Mantener filtro por tipo TRABAJADOR
    if (tipo !== "TRABAJADOR" || !fechaNaci) return;

    try {
      const [diaNac, mesNac] = fechaNaci.split("/").map(Number);
      const diaTicket = createAt.date();
      const mesTicket = createAt.month() + 1;

      // Si el ticket es de cumpleaños, lo contamos
      if (diaNac === diaTicket && mesNac === mesTicket) {
        count++;
      }
    } catch (error) {
      console.error("Error procesando fecha:", fechaNaci, error);
    }
  });

  return count;
};

const normalizeMealName = (name) => (name || "").toString().trim();

const getAvailableMealNames = (tickets = [], availableMeals = []) => {
  const fromTickets = tickets
    .map((item) => normalizeMealName(item?.comida || item?.pre_comidas?.nombre))
    .filter(Boolean);
  const fromCatalog = (availableMeals || []).map(normalizeMealName).filter(Boolean);
  return Array.from(new Set([...fromCatalog, ...fromTickets]));
};

const getConsolidadoData = (filteredData) => {
  const cumpleaneros = countCumpleanerosTickets(filteredData);
  return {
    cuadrillas: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "cuadrilla"
    ).length,
    trabajadores: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "trabajador"
    ).length,
    estudiantes: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "E"
    ).length,
    proveedores: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "P"
    ).length,
    visitantes: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "V"
    ).length,
    guardias: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "G"
    ).length,
    seafman: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "S"
    ).length,
    trimare: filteredData.filter(
      (d) => d?.pre_usuarios?.pre_tipos_usuarios?.code === "T"
    ).length,
    noIdentificados: filteredData.filter(
      (d) =>
        !d?.pre_usuarios?.pre_tipos_usuarios?.code ||
        d.pre_usuarios.pre_tipos_usuarios.code === "expatriado"
    ).length,
    cumpleaneros,
  };
};

const CONSOLIDADO_TOP_MARGIN = 20;
const CONSOLIDADO_BOTTOM_MARGIN = 14;
const CONSOLIDADO_GAP = 10;
const CONSOLIDADO_MIN_BLOCK_HEIGHT = 92;

const drawConsolidadoTable = (doc, title, resumen, startY) => {
  doc.setFontSize(13);
  doc.text(title, 14, startY);
  const tableStartY = startY + 4;

  const total =
    resumen.cuadrillas +
    resumen.trabajadores +
    resumen.estudiantes +
    resumen.proveedores +
    resumen.visitantes +
    resumen.guardias +
    resumen.seafman +
    resumen.trimare +
    resumen.noIdentificados;

  const headers = [["Categoría", "Cantidad"]];
  let labelTrabajadores = "";
  if (resumen.cumpleaneros > 0) {
    labelTrabajadores = `(${resumen.cumpleaneros})`;
    headers[0].push("Cumpleañeros");
  }
  const body = [
    ["Cuadrillas", resumen.cuadrillas],
    ["Trabajadores", resumen.trabajadores, labelTrabajadores],
    ["Estudiantes", resumen.estudiantes],
    ["Proveedores", resumen.proveedores],
    ["Visitantes", resumen.visitantes],
    ["Guardias", resumen.guardias],
    ["Seafman", resumen.seafman],
    ["Trimare", resumen.trimare],
    ["NO identificados", resumen.noIdentificados],
    ["Total", total],
  ];

  autoTable(doc, {
    startY: tableStartY,
    head: headers,
    body,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 1.5, halign: "center" },
    headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    columnStyles: {
      0: { cellWidth: 90, halign: "left" },
      1: { cellWidth: 30, halign: "center" },
    },
  });

  return doc.lastAutoTable.finalY;
};

const drawConsolidadoSections = (doc, sections, firstPageStartY = CONSOLIDADO_TOP_MARGIN) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxY = pageHeight - CONSOLIDADO_BOTTOM_MARGIN;
  let startY = firstPageStartY;

  sections.forEach((section, index) => {
    const remaining = maxY - startY;
    if (index > 0 && remaining < CONSOLIDADO_MIN_BLOCK_HEIGHT) {
      doc.addPage();
      startY = CONSOLIDADO_TOP_MARGIN;
    }

    const finalY = drawConsolidadoTable(
      doc,
      section.title,
      section.resumen,
      startY
    );
    startY = finalY + CONSOLIDADO_GAP;
  });
};

export const buildTotalTicketsPDFBlob = (data, dateFilter, availableMeals = []) => {
  if (data.length === 0) {
    alert("No hay registros para exportar a PDF");
    return;
  }

  const doc = new jsPDF();
  // Encabezado principal
  let fechaTexto = new Date();
  if (
    dayjs(dateFilter[0]).format("YYYY-MM-DD") ===
    dayjs(dateFilter[1]).format("YYYY-MM-DD")
  ) {
    fechaTexto = dayjs(dateFilter[0]).format("YYYY-MM-DD");
  } else {
    fechaTexto = `${dayjs(dateFilter[0]).format("YYYY-MM-DD")} - ${dayjs(
      dateFilter[1]
    ).format("YYYY-MM-DD")}`;
  }
  doc.setFontSize(18);
  doc.text("Reporte de Comidas", 75, 12);
  doc.text(fechaTexto, 130, 12);

  const mealNames = getAvailableMealNames(data, availableMeals);
  const sections = mealNames.map((mealName) => {
    const mealData = data.filter(
      (item) => normalizeMealName(item.comida) === mealName
    );
    return {
      title: `${mealName} CONFIRMADOS`,
      resumen: getConsolidadoData(mealData),
    };
  });
  sections.push({
    title: "TOTAL GENERAL",
    resumen: getConsolidadoData(data),
  });
  // Empieza debajo del encabezado; el layout fluye por altura real de cada tabla
  drawConsolidadoSections(doc, sections, 18);

  const blob = doc.output("blob");
  const filename = `reporte_consolidado_${dayjs().format(
    "YYYY-MM-DD_HH-mm-ss"
  )}.pdf`;

  return { blob, filename };
};

// Reporte de Comidas
export const exportTotalTicketsPDF = (data, dateFilter, availableMeals = []) => {
  if (data.length === 0) {
    alert("No hay registros para exportar a PDF");
    return;
  }

  const doc = new jsPDF();
  let fechaTexto = new Date();
  if (
    dayjs(dateFilter[0]).format("YYYY-MM-DD") ===
    dayjs(dateFilter[1]).format("YYYY-MM-DD")
  ) {
    fechaTexto = dayjs(dateFilter[0]).format("DD-MM-YYYY");
  } else {
    fechaTexto = `${dayjs(dateFilter[0]).format("DD-MM-YYYY")} - ${dayjs(
      dateFilter[1]
    ).format("DD-MM-YYYY")}`;
  }

  // Encabezado principal
  doc.setFontSize(18);
  doc.text("Reporte de Comidas", 60, 12);
  doc.text(fechaTexto, 130, 12);

  const mealNames = getAvailableMealNames(data, availableMeals);
  const sections = mealNames.map((mealName) => {
    const mealData = data.filter(
      (item) => normalizeMealName(item.comida) === mealName
    );
    return {
      title: `${mealName} CONFIRMADOS`,
      resumen: getConsolidadoData(mealData),
    };
  });
  sections.push({
    title: "TOTAL GENERAL",
    resumen: getConsolidadoData(data),
  });
  drawConsolidadoSections(doc, sections, 18);

  doc.save(`reporte_consolidado_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
};

// GESTOR DE TICKETS
export const exportExcel = (filteredTickets, filters) => {
  const data = filteredTickets.map((item) => {
    return {
      Periodo: item.pre_periodos.nombre,
      "Fecha y hora": dayjs(item.create_at)
        .tz("America/Guayaquil")
        .format("YYYY-MM-DD HH:mm"),
      Identificacion: item.pre_usuarios.usuario,
      Apellidos: item.pre_usuarios.apellidos,
      Nombres: item.pre_usuarios.nombres,
      Tipo: item.pre_usuarios?.pre_tipos_usuarios?.tipo || "",
      "Codigo Trabajador": item.pre_usuarios?.code ?? "",
      Comida: item.comida || item.pre_comidas?.nombre || "",
    };
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Reporte de comidas");
  XLSX.writeFile(
    wb,
    `reporte_tickets_${dayjs(filters.startDate).format("YYYY-MM-DD")}_${dayjs(
      filters.endDate
    ).format("YYYY-MM-DD")}.xlsx`
  );
};

export const exportTicketsPDF = (filteredTickets, filters, availableMeals = []) => {
  const mealNames = getAvailableMealNames(filteredTickets, availableMeals);
  const getResumenPorCodigo = (tickets) => {
    const resumenMap = new Map();

    for (const t of tickets) {
      const code = String(t.pre_usuarios?.code || "SIN CÓDIGO");

      if (!resumenMap.has(code)) {
        const counts = {};
        mealNames.forEach((meal) => {
          counts[meal] = 0;
        });
        resumenMap.set(code, counts);
      }

      const mealName = normalizeMealName(t.comida || t?.pre_comidas?.nombre);
      const resumen = resumenMap.get(code);
      if (mealName) {
        if (typeof resumen[mealName] !== "number") resumen[mealName] = 0;
        resumen[mealName] += 1;
      }
    }

    return Array.from(resumenMap.entries()).map(([code, counts]) => {
      const total = Object.values(counts).reduce((acc, value) => acc + value, 0);
      return { code, counts, total };
    });
  };

  const createResumenCodigosTable = (titulo, tickets) => {
    doc.setFontSize(14);
    doc.text(titulo, 14, startY);
    startY += 3;

    const resumen = getResumenPorCodigo(tickets);
    const headers = [["Código", ...mealNames, "Total"]];
    const body = resumen.map((r) => [
      r.code,
      ...mealNames.map((meal) => r.counts[meal] || 0),
      r.total,
    ]);
    const dynamicColumnStyles = { 0: { cellWidth: 50, halign: "left" } };
    const totalColumns = headers[0].length;
    for (let i = 1; i < totalColumns; i++) {
      dynamicColumnStyles[i] = { halign: "center" };
    }

    autoTable(doc, {
      startY,
      head: headers,
      body,
      theme: "grid",
      styles: { fontSize: 10, halign: "center" },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: dynamicColumnStyles,
    });

    startY = doc.lastAutoTable.finalY + 10;
  };

  // Preparar datos
  const data = filteredTickets.map((item) => [
    item.pre_periodos.nombre,
    dayjs(item.create_at).tz("America/Guayaquil").format("YYYY-MM-DD HH:mm"),
    item.pre_usuarios.usuario,
    item.pre_usuarios.apellidos,
    item.pre_usuarios.nombres,
    item.pre_usuarios?.pre_tipos_usuarios?.tipo || "",
    item.pre_usuarios?.code ?? "",
    item.comida || item.pre_comidas?.nombre || "",
  ]);

  // Crear PDF
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });
  let startY = 20;

  createResumenCodigosTable(
    "CONSOLIDADO POR CÓDIGO DE TRABAJADOR",
    filteredTickets
  );
  // que inicie en la siguiente hoja
  doc.addPage();
  startY = 20;

  // Título
  const title = `Reporte de Tickets (${dayjs(filters.startDate).format(
    "YYYY-MM-DD"
  )} - ${dayjs(filters.endDate).format("YYYY-MM-DD")})`;
  doc.setFontSize(16);
  doc.text(title, 15, 15);

  // Encabezados de tabla
  const headers = [
    "Periodo",
    "Fecha y Hora",
    "Identificación",
    "Apellidos",
    "Nombres",
    "Tipo",
    "Código Trabajador",
    "Comida",
  ];

  // Generar tabla
  autoTable(doc, {
    head: [headers],
    body: data,
    startY: startY,
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: "bold",
    },
  });

  // Guardar PDF
  doc.save(
    `reporte_tickets_${dayjs(filters.startDate)
      .tz("America/Guayaquil")
      .format("YYYY-MM-DD")}_${dayjs(filters.endDate).format("YYYY-MM-DD")}.pdf`
  );
};

export const exportExcelRecuento = (resumenData, filters) => {
  const data = resumenData.map((item) => {
    return {
      Codigo: item.code,
      "Cantidad Almuerzo": item.countAlmuerzo,
      "Cantidad Merienda": item.countMerienda,
    };
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Reporte de comidas");
  XLSX.writeFile(
    wb,
    `reporte_reconteo_${dayjs(filters.startDate)
      .tz("America/Guayaquil")
      .format("YYYY-MM-DD")}_${dayjs(filters.endDate).format(
      "YYYY-MM-DD"
    )}.xlsx`
  );
};

export const getFilenameFromKey = (key) => {
  const last = (key || "").split("/").pop() || "reporte.pdf";
  return last.endsWith(".pdf") ? last : `${last}.pdf`;
};

export const downloadFromSignedUrl = async (signedUrl, fallbackName) => {
  try {
    const res = await fetch(signedUrl);
    if (!res.ok) throw new Error("No se pudo descargar el archivo");

    const blob = await res.blob();

    // Intentar obtener nombre desde Content-Disposition, si viene
    const cd = res.headers.get("content-disposition");
    let filename = fallbackName;
    if (cd) {
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
      if (m) filename = decodeURIComponent(m[1] || m[2]);
    }

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (e) {
    // Fallback: abrir en nueva pestaña (iOS/Safari u otros casos)
    window.open(signedUrl, "_blank", "noopener,noreferrer");
  }
};
